import React, { memo, useCallback, useMemo } from 'react';
import { Keyboard, TouchableOpacity, View } from 'react-native';
import Text from 'design-system/Text';
import StatusIcon from 'design-system/StatusIcon';
import { hitSlopS, IconArrow, rem, Spacer } from 'design-system';
import IconTooltip from 'design-system/icons/IconTooltip';
import BlurAmountView from 'design-system/BlurAmountView';
import IconEmmaWallet from 'design-system/icons/IconEmmaWallet';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import ToolTip from 'design-system/ToolTip';
import { selectTradingAccountFromId } from 'features/invest/reducers/selectors';
import AvailableModal, { AVAILABLE_TITLE, getAvailableExplanation } from './AvailableModal';
import { getTradingAccountName } from '../utils';
import { isWeb } from '../../../constants';
const SecclAccountRow = ({ onPress, tradingAccountId, isPickerModalOpen }) => {
    const styles = useStyles(styleSet);
    const tradingAccount = useAppSelector((state) => selectTradingAccountFromId(state, tradingAccountId));
    const pendingTopupsInfo = useAppSelector((state) => state.invest.pendingTopUpInfoForAccountId?.[tradingAccountId]);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const onInfoButtonPress = useCallback(() => {
        Keyboard.dismiss();
        AvailableModal.show(tradingAccountId);
    }, [tradingAccountId]);
    const totalPendingTopupsAmount = useMemo(() => ({
        value: pendingTopupsInfo?.pendingTopUps?.reduce((acc, curr) => acc + curr.amount.value, 0) || 0,
        currency: pendingTopupsInfo?.pendingTopUps?.[0]?.amount.currency || 'GBP',
    }), [pendingTopupsInfo]);
    if (tradingAccount?.broker !== 'seccl') {
        return null;
    }
    const disabled = !onPress;
    return (<TouchableBounceCard onDark disabled={disabled} noMarginTop noMarginBottom style={styles.cardBtn} onPress={onPress}>
      <View style={styles.row}>
        <IconEmmaWallet size={rem(44)}/>
        <Spacer w={16}/>
        <View style={styles.flex}>
          <Text Text-16 numberOfLines={1}>
            {getTradingAccountName(tradingAccount)}
          </Text>
          <View style={styles.row}>
            <Text TextThin-14 Secondary>
              Available{' '}
            </Text>
            <BlurAmountView enabled={scrambled}>
              <Text NumbersThin-14 Secondary>
                {formatAmount(tradingAccount.availableToInvest?.value || 0, tradingAccount.currency, 2)}
              </Text>
            </BlurAmountView>
            <Spacer w={4}/>
            {isWeb ? (
        // On web the explanation shows on hover, like other info icons
        <ToolTip small colorKey="secondary" title={AVAILABLE_TITLE} subtitle={getAvailableExplanation(tradingAccount?.accountType)}/>) : (<TouchableOpacity onPress={onInfoButtonPress} hitSlop={hitSlopS}>
                <IconTooltip colorKey="secondary" width={16} height={16}/>
              </TouchableOpacity>)}
          </View>
          {pendingTopupsInfo?.hasPendingTopups ? (<View style={styles.row}>
              <StatusIcon state="in-progress" width={16} height={16}/>
              <Spacer w={8}/>
              <Text TextThin-14 StatusInformational>
                Pending{' '}
              </Text>
              {!!totalPendingTopupsAmount.value && (<BlurAmountView enabled={scrambled}>
                  <Text NumbersThin-14 StatusInformational>
                    {formatAmount(totalPendingTopupsAmount.value, totalPendingTopupsAmount.currency, 2)}
                  </Text>
                </BlurAmountView>)}
            </View>) : null}
        </View>
        {!disabled && (<View style={styles.arrow}>
            <IconArrow direction={isPickerModalOpen ? 'up' : 'down'}/>
          </View>)}
      </View>
    </TouchableBounceCard>);
};
const styleSet = createStyleSheets(() => ({
    cardBtn: {
        paddingVertical: rem(24),
        paddingHorizontal: rem(16),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
    arrow: {
        width: rem(10),
        marginLeft: rem(16),
    },
}));
export default memo(SecclAccountRow);
